import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, MessageCircle, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, openWhatsApp, track } from "@/lib/api";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import Timeline from "@/components/Timeline";

function WaButton({ label, message, testid, className = "" }) {
  const { settings } = useSite();
  const number = settings?.contact?.whatsapp || "";
  return (
    <Button
      asChild
      className={`rounded-full bg-accent hover:bg-accent-bright text-white font-semibold shadow-[0_0_28px_rgba(124,58,237,0.45)] hover:shadow-[0_0_42px_rgba(168,85,247,0.6)] transition-all duration-300 hover:-translate-y-0.5 ${className}`}
      data-testid={testid}
    >
      <a
        href={`https://wa.me/${String(number).replace(/\D/g, "")}?text=${encodeURIComponent(message)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("whatsapp_click", window.location.pathname)}
      >
        <MessageCircle className="h-4 w-4 mr-2" />
        {label}
      </a>
    </Button>
  );
}

function PreviewDialog({ title, image, children }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="glass-strong border-white/10 max-w-3xl p-2 sm:p-3" data-testid="sample-preview-dialog">
        <DialogHeader className="px-4 pt-3 pb-2">
          <DialogTitle className="font-display text-white text-base text-left">{title}</DialogTitle>
        </DialogHeader>
        <div className="spot-frame aspect-[16/10]">
          <img src={mediaUrl(image)} alt={title} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SampleGallery({ samples, settings, heading }) {
  const list = samples || [];
  if (list.length === 0) return null;
  const select = (s) => {
    const tpl = settings?.templates?.design_selection || "Hi! I selected the {design} design.";
    openWhatsApp(settings?.contact?.whatsapp || "", tpl.replace("{design}", s.title), { trackPath: "/select-design" });
  };
  return (
    <div>
      {heading && <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-5">{heading}</h3>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((s, i) => (
          <div key={s.id || i} className="glass rounded-2xl overflow-hidden group" data-testid={`sample-card-${i}`}>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={mediaUrl(s.image_url)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {s.badge && <span className="absolute top-3 left-3 font-mono-label text-[8px] bg-accent text-white rounded-full px-2.5 py-1">{s.badge}</span>}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="font-display text-sm font-semibold text-white">{s.title}</h4>
                <span className="text-xs font-semibold text-accent-bright">{s.price}</span>
              </div>
              <p className="text-[11px] text-subtle mb-3">{s.category}</p>
              <div className="flex gap-2">
                <PreviewDialog title={s.title} image={s.image_url}>
                  <button className="flex-1 text-xs font-semibold px-3 py-2 rounded-lg border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors" data-testid={`sample-preview-btn-${i}`}>
                    Preview
                  </button>
                </PreviewDialog>
                <button onClick={() => select(s)} className="flex-1 text-xs font-semibold px-3 py-2 rounded-lg bg-accent hover:bg-accent-bright text-white transition-colors" data-testid={`sample-select-btn-${i}`}>
                  Select Design
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SiteGate({ content, samples, settings }) {
  const [hasSite, setHasSite] = useState(null);
  return (
    <section className="py-14 sm:py-20" data-testid="website-gate-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="glass rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="ambient-glow w-[420px] h-[240px] -top-24 left-1/2 -translate-x-1/2 opacity-50" />
          <p className="font-mono-label text-[11px] text-accent-bright mb-4">Quick Question</p>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mb-8">{content?.gate?.question || "Do you already have a website?"}</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => { setHasSite(true); track("button_click", "/gate-yes"); }}
              className={`px-7 py-3 rounded-full text-sm font-semibold border transition-all duration-300 ${hasSite === true ? "bg-accent border-accent text-white shadow-[0_0_24px_rgba(124,58,237,0.5)]" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
              data-testid="gate-yes-btn"
            >
              Yes, I do
            </button>
            <button
              onClick={() => { setHasSite(false); track("button_click", "/gate-no"); }}
              className={`px-7 py-3 rounded-full text-sm font-semibold border transition-all duration-300 ${hasSite === false ? "bg-accent border-accent text-white shadow-[0_0_24px_rgba(124,58,237,0.5)]" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
              data-testid="gate-no-btn"
            >
              Not yet
            </button>
          </div>
          {hasSite === true && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 text-left glass-strong rounded-2xl p-6 sm:p-8 border border-accent/25" data-testid="gate-yes-panel">
              <p className="font-mono-label text-[10px] text-accent-bright mb-3">Recommended Next Step</p>
              <p className="text-sm sm:text-base text-mist leading-relaxed mb-6">{content?.gate?.yes_note}</p>
              <Button asChild className="rounded-full bg-white text-[#0B0B14] hover:bg-mist font-semibold" data-testid="gate-ad-setup-link">
                <Link to="/services/ad-setup">
                  {content?.gate?.yes_cta_label || "Explore Ad Setup"} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </motion.div>
          )}
          {hasSite === false && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 text-left" data-testid="gate-no-panel">
              <p className="text-sm sm:text-base text-mist leading-relaxed mb-6">{content?.gate?.no_note}</p>
              <SampleGallery samples={samples} settings={settings} />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

function FeaturesGrid({ features }) {
  if (!features || features.length === 0) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="features-grid-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="What's Included" title="Every plan, all of this" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f, i) => (
            <Reveal key={i} delay={(i % 4) * 0.05}>
              <div className="h-full glass rounded-2xl p-5 flex items-start gap-3 transition-all duration-300 hover:border-accent/40 hover:-translate-y-1" data-testid={`feature-item-${i}`}>
                <span className="h-6 w-6 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-accent-bright" />
                </span>
                <p className="text-sm text-white/90 leading-relaxed">{f}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PaymentSteps({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <section className="py-16 sm:py-20 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="payment-steps-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="How It Works" title="5-step payment process" sub="No surprises — everything is confirmed in writing before you pay." />
        <Timeline steps={steps.map((s, i) => ({ ...s, num: i + 1 }))} />
      </div>
    </section>
  );
}

function PricingBreakdown({ rows, notes }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="pricing-breakdown-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <SectionHead eyebrow="Transparent Pricing" title="Exactly where your money goes" />
        <Reveal>
          <div className="glass rounded-3xl overflow-hidden">
            {rows.map((r, i) => (
              <div key={i} className={`flex items-center justify-between gap-4 px-6 sm:px-8 py-5 ${i !== rows.length - 1 ? "border-b border-white/[0.07]" : ""}`} data-testid={`pricing-row-${i}`}>
                <span className="text-sm text-mist">{r.label}</span>
                <span className="text-sm font-semibold text-white text-right">{r.value}</span>
              </div>
            ))}
          </div>
          {notes && <p className="mt-5 text-xs text-subtle leading-relaxed text-center">{notes}</p>}
        </Reveal>
      </div>
    </section>
  );
}

function IndustriesTags({ industries }) {
  if (!industries || industries.length === 0) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="industries-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <SectionHead eyebrow="Who It's For" title="Built for businesses that live on leads" />
        <div className="flex flex-wrap justify-center gap-3">
          {industries.map((tag, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <span className="inline-block glass rounded-full px-5 py-2.5 text-sm font-medium text-white/90 hover:border-accent/50 hover:text-white transition-all cursor-default" data-testid={`industry-tag-${i}`}>
                {tag}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function UgcLibrary({ models }) {
  const { settings } = useSite();
  const list = models || [];
  if (list.length === 0) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="ugc-library-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Creator Library" title="Pick your UGC creator" sub="Every creator is briefed, trained and quality-checked by our studio." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {list.map((m, i) => (
            <Reveal key={m.id || i} delay={(i % 3) * 0.06}>
              <div className="glass rounded-3xl overflow-hidden group" data-testid={`ugc-model-card-${i}`}>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={mediaUrl(m.photo_url)} alt={m.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B0B14] to-transparent z-10" />
                  <span className={`absolute top-3 right-3 z-10 font-mono-label text-[8px] rounded-full px-2.5 py-1 border ${m.availability === "Available" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-rose-400 bg-rose-400/10 border-rose-400/30"}`} data-testid={`ugc-availability-${i}`}>
                    {m.availability}
                  </span>
                  {m.video_url && (
                    <a href={m.video_url} target="_blank" rel="noopener noreferrer" className="absolute bottom-3 right-3 z-10 glass rounded-full p-2.5 text-white hover:border-accent/60 transition-colors" data-testid={`ugc-video-link-${i}`} aria-label={`Watch ${m.name}'s reel`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <h4 className="font-display text-base font-semibold text-white">{m.name}</h4>
                    <span className="font-display text-sm font-bold text-accent-bright">{m.price}</span>
                  </div>
                  <p className="text-xs text-subtle mb-4">{m.language}</p>
                  <button
                    onClick={() => {
                      const tpl = settings?.templates?.ugc_selection || "Hi! I want to book {model} ({language}).";
                      openWhatsApp(settings?.contact?.whatsapp || "", tpl.replace("{model}", m.name).replace("{language}", m.language), { trackPath: "/choose-model" });
                    }}
                    className="w-full text-xs font-semibold px-4 py-2.5 rounded-xl bg-accent hover:bg-accent-bright text-white transition-colors flex items-center justify-center gap-2"
                    data-testid={`ugc-choose-model-btn-${i}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> Choose Model
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceProofs({ proofs, slug }) {
  const list = (proofs || []).filter((p) => p.service_slug === slug);
  if (list.length === 0) return null;
  return (
    <section className="py-16 sm:py-20 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="service-proofs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Receipts" title="Results from this service" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((p, i) => (
            <Reveal key={p.id || i} delay={i * 0.06}>
              <div className="spot-frame aspect-[4/3]" data-testid={`service-proof-card-${i}`}>
                <img src={mediaUrl(p.image_url)} alt={p.title} loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 z-10 p-5 bg-gradient-to-t from-[#0B0B14]/95 to-transparent">
                  {p.metric && <span className="inline-block font-mono-label text-[9px] text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-full px-3 py-1 mb-2">{p.metric}</span>}
                  <p className="text-sm font-semibold text-white leading-snug">{p.title}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { settings, services, samples, ugc_models, proofs } = useSite();
  const service = (services || []).find((s) => s.slug === slug);
  const content = settings?.service_pages?.[slug] || {};

  if (!service && services && services.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center glass rounded-3xl p-12 max-w-md mx-4">
          <h1 className="font-display text-2xl font-bold text-white mb-3">Service not found</h1>
          <p className="text-sm text-mist mb-6">The service you're looking for doesn't exist or was renamed.</p>
          <Button asChild className="rounded-full bg-accent text-white font-semibold" data-testid="service-back-home-btn">
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isWa = String(settings?.cta_banner?.button_href || "").startsWith("whatsapp:");
  const finalCtaHref = isWa
    ? `https://wa.me/${String(settings?.contact?.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.templates?.general || "Hi!")}`
    : settings?.cta_banner?.button_href || "/contact";

  return (
    <div className="pt-24 sm:pt-28" data-testid="service-detail-page">
      <section className="relative py-14 sm:py-20 overflow-hidden">
        <div className="ambient-glow w-[560px] h-[320px] -top-32 -left-32 opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal>
            <div className="flex items-center gap-2 text-xs text-subtle mb-6 font-mono-label">
              <Link to="/" className="hover:text-mist transition-colors" data-testid="service-breadcrumb-home">Home</Link>
              <span>/</span>
              <span className="text-accent-bright">{service?.title}</span>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                {content.badge && <span className="inline-block font-mono-label text-[10px] text-accent-bright border border-accent/30 bg-accent/10 rounded-full px-4 py-1.5 mb-6" data-testid="service-badge">{content.badge}</span>}
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]" data-testid="service-heading">{content.heading || service?.title}</h1>
                <p className="mt-5 text-sm sm:text-base text-mist leading-relaxed max-w-2xl" data-testid="service-sub">{content.sub}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.1}>
                <div className="glass rounded-3xl p-6 sm:p-7" data-testid="service-price-card">
                  <p className="font-mono-label text-[10px] text-subtle mb-2">Starting at</p>
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-3xl sm:text-4xl font-bold text-white" data-testid="service-price">{content.price || service?.price}</span>
                    {content.strike_price && <span className="text-base text-subtle line-through">{content.strike_price}</span>}
                  </div>
                  <div className="mt-5">
                    <WaButton label="Discuss on WhatsApp" message={settings?.templates?.general || "Hi!"} testid="service-whatsapp-cta" className="w-full" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {slug === "website-development" && (
        <>
          <SiteGate content={content} samples={samples} settings={settings} />
          <FeaturesGrid features={content.features} />
          <PaymentSteps steps={content.payment_steps} />
        </>
      )}

      {slug === "ad-setup" && (
        <>
          {content.process && content.process.length > 0 && (
            <section className="py-16 sm:py-20" data-testid="service-process-section">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow="The Process" title="How we run your ads" />
                <Timeline steps={content.process} />
              </div>
            </section>
          )}
          <PricingBreakdown rows={content.pricing_rows} notes={content.pricing_notes} />
        </>
      )}

      {slug === "lead-generation" && (
        <>
          <IndustriesTags industries={content.industries} />
          {content.process && content.process.length > 0 && (
            <section className="py-16 sm:py-20" data-testid="service-process-section">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow="The Pipeline" title="From click to confirmed lead" />
                <Timeline steps={content.process} />
              </div>
            </section>
          )}
          {content.pricing_notes && (
            <section className="pb-16 sm:pb-20" data-testid="service-pricing-notes-section">
              <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <Reveal>
                  <div className="glass rounded-2xl p-6 sm:p-8 text-center">
                    <p className="font-mono-label text-[10px] text-accent-bright mb-3">Pricing Note</p>
                    <p className="text-sm text-mist leading-relaxed">{content.pricing_notes}</p>
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </>
      )}

      {slug === "ugc-videos" && (
        <>
          {content.process && content.process.length > 0 && (
            <section className="py-16 sm:py-20" data-testid="service-process-section">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow="How Ordering Works" title="From brief to final cut" />
                <Timeline steps={content.process} />
              </div>
            </section>
          )}
          <UgcLibrary models={ugc_models} />
          {content.pricing_notes && (
            <section className="pb-16 sm:pb-20">
              <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <Reveal>
                  <div className="glass rounded-2xl p-6 sm:p-8 text-center">
                    <p className="font-mono-label text-[10px] text-accent-bright mb-3">Pricing Note</p>
                    <p className="text-sm text-mist leading-relaxed">{content.pricing_notes}</p>
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </>
      )}

      {(samples && samples.length > 0 && slug === "website-development") && (
        <section className="py-16 sm:py-20 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="service-samples-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Gallery" title={content.gallery_heading || "Website Samples"} sub={content.gallery_sub} />
            <SampleGallery samples={samples} settings={settings} />
          </div>
        </section>
      )}

      <ServiceProofs proofs={proofs} slug={slug} />

      <section className="relative py-20 sm:py-24 overflow-hidden" data-testid="service-cta-section">
        <div className="ambient-glow w-[600px] h-[320px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-60" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-5">{settings?.cta_banner?.heading || "Ready to start?"}</h2>
            <a
              href={finalCtaHref}
              target={isWa ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-8 py-4 shadow-[0_0_36px_rgba(124,58,237,0.5)] transition-all duration-300 hover:-translate-y-1"
              data-testid="service-cta-button"
              onClick={() => track("button_click", `/service-${slug}-cta`)}
            >
              {settings?.cta_banner?.button_label || "Get Started"} <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
